import type { MouseTouchEvent } from '@ad-lucy/hooks/tracking/types';
import { useCallback, useRef } from 'react';

type NullableNumber = number | null;

export interface CoordinateProps {
  mouseX: NullableNumber;
  mouseY: NullableNumber;
}

export type UseCoordinatesReturnType = (
  e?: MouseTouchEvent,
  additionalOptions?: CoordinateProps,
) => CoordinateProps;

export const useCoordinates = (): UseCoordinatesReturnType => {
  const lastMuteClickPosition = useRef<CoordinateProps>({
    mouseX: null,
    mouseY: null,
  });

  const getCoordinates = useCallback(
    (e?: MouseTouchEvent, additionalOptions?: CoordinateProps): CoordinateProps => {
      const { mouseX, mouseY } = lastMuteClickPosition.current;

      const roundedX = (value: number | undefined | null): number | null => {
        return typeof value === 'number' ? Math.round(value) : null;
      };

      const getMouseCoordinates = (): CoordinateProps => {
        return {
          mouseX: mouseX && roundedX(mouseX),
          mouseY: mouseY && roundedX(mouseY),
        };
      };

      if (e && 'touches' in e && e.touches && e.touches.length > 0) {
        const evt = e;
        const roundedTouchX = roundedX(evt.touches[0]?.clientX);
        const roundedTouchY = roundedX(evt.touches[0]?.clientY);

        return {
          mouseX: mouseX || roundedTouchX,
          mouseY: mouseY || roundedTouchY,
        };
      }

      if (additionalOptions?.mouseX !== undefined && additionalOptions?.mouseY !== undefined) {
        const roundedAdditionalX = roundedX(additionalOptions.mouseX);
        const roundedAdditionalY = roundedX(additionalOptions.mouseY);

        return {
          mouseX: mouseX || roundedAdditionalX,
          mouseY: mouseY || roundedAdditionalY,
        };
      }

      if (e) {
        const roundedEventX = roundedX(e.clientX);
        const roundedEventY = roundedX(e.clientY);

        return {
          mouseX: mouseX || roundedEventX,
          mouseY: mouseY || roundedEventY,
        };
      }

      return getMouseCoordinates();
    },
    [],
  );

  return getCoordinates;
};
